import { RecordSource, SyncDataType } from '../api/types';

/** Seconds to "h:mm:ss" (or "m:ss" under an hour). */
export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** Seconds per km to "m:ss /km". */
export function formatPace(secondsPerKm: number | null | undefined): string {
  if (secondsPerKm == null || !Number.isFinite(secondsPerKm) || secondsPerKm <= 0) return '—';
  return `${formatDuration(secondsPerKm)} /km`;
}

/** Meters to kilometres with two decimals. */
export function formatDistance(meters: number | null | undefined): string {
  if (meters == null || !Number.isFinite(meters)) return '—';
  return `${(meters / 1000).toFixed(2)} km`;
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toFixed(digits);
}

/** Seconds to decimal hours, e.g. 27000 to "7.5 h". */
export function formatHours(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  return `${(seconds / 3600).toFixed(1)} h`;
}

/** Date (YYYY-MM-DD) or ISO timestamp to a short local date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Parses "m:ss", "h:mm:ss" or plain seconds into seconds. Returns null when invalid. */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const parts = trimmed.split(':').map(p => (p === '' ? NaN : Number(p)));
  if (parts.length < 2 || parts.length > 3 || parts.some(p => !Number.isInteger(p) || p < 0)) return null;
  const [h, m, s] = parts.length === 3 ? parts : [0, parts[0], parts[1]];
  if (m > 59 || s > 59) return null;
  return h * 3600 + m * 60 + s;
}

/** Garmin activity type keys the UI knows about. Other keys are shown as their name, humanized. */
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  running: 'Run',
  trail_running: 'Trail run',
  treadmill_running: 'Treadmill run',
  walking: 'Walk',
  hiking: 'Hike',
  cycling: 'Ride',
  indoor_cycling: 'Indoor ride',
};

/** Activity types offered by the type filter. */
export const ACTIVITY_TYPE_OPTIONS = Object.keys(ACTIVITY_TYPE_LABELS);

/** Run types, for pace and heart rate trends. */
export const RUN_TYPES = ['running', 'trail_running', 'treadmill_running'];

export function activityTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Activity';
  if (ACTIVITY_TYPE_LABELS[type]) return ACTIVITY_TYPE_LABELS[type];
  const words = type.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const SOURCE_LABELS: Record<RecordSource, string> = {
  auto: 'Auto',
  adjusted: 'Adjusted',
  manual: 'Manual',
};

export const SYNC_TYPE_LABELS: Record<SyncDataType, string> = {
  ACTIVITIES: 'Activities',
  DAILY: 'Daily health',
  SLEEP: 'Sleep',
  HRV: 'HRV',
  BODY_BATTERY: 'Body battery',
  WEIGHT: 'Weight',
};

/** Local calendar date (YYYY-MM-DD) for a Date. */
export function isoLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local calendar date (YYYY-MM-DD) `days` before today. */
export function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return isoLocal(d);
}

/** Whole days from one YYYY-MM-DD date to another (to minus from). */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T12:00:00`);
  const b = Date.parse(`${to}T12:00:00`);
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

/** Short axis label for a YYYY-MM-DD string: "MM-DD". */
export function shortDate(iso: string): string {
  return iso.slice(5);
}

/** The YYYY-MM-DD date `days` days after `iso` (negative goes back). */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return isoLocal(d);
}
